import Cookies from "js-cookie";

const SELECT_BUTTON_CLASS = "js-chit-chats-print-select-printer";
const ADVANCED_TOGGLE_CLASS = "js-chit-chats-print-advanced-printers-toggle";
const ADVANCED_LIST_CLASS = "js-chit-chats-print-advanced-printers";
const PREVIEW_FORMAT = "pdf";
const ZPL_FORMAT = "zpl";

function printerFormat(name) {
  return /^(Zebra|ZDesigner|raw|ZD|ZP)/i.test(name || "") ? ZPL_FORMAT : PREVIEW_FORMAT;
}

// CUPS substitutes underscores for spaces in queue names ("Zebra_ZP_450"),
// so labels look messy in the picker. Transform purely for rendering; the raw
// name still lives in the cookie and data-attrs because print routing needs
// byte-for-byte equality with the OS-reported name.
function displayName(name) {
  return (name || "").replace(/_/g, " ");
}

function previewMethod(messages) {
  return {
    id: "",
    name: messages.previewName,
    format: PREVIEW_FORMAT,
    isPreview: true
  };
}

function readPrintMethodCookie() {
  var encoded = Cookies.get("printer");
  if (!encoded) {
    return null;
  }

  try {
    return JSON.parse(window.atob(encoded));
  } catch (_e) {
    return null;
  }
}

function writePrintMethodCookie(method) {
  var info = {
    name: method.name,
    format: method.format || printerFormat(method.name),
    deviceId: method.id || ""
  };
  Cookies.set("printer", window.btoa(JSON.stringify(info)), { expires: (365 * 20), path: "/" });
  return info;
}

function printMethodMatches(savedMethod, method) {
  if (!savedMethod || !method) {
    return false;
  }
  if (savedMethod.format !== ZPL_FORMAT || method.format !== ZPL_FORMAT) {
    return savedMethod.format === method.format;
  }
  return savedMethod.deviceId === method.id || savedMethod.name === method.name;
}

function selectedPrintMethodText(savedMethod, messages) {
  if (!savedMethod || !savedMethod.name) {
    return messages.selectedEmpty;
  }
  if (savedMethod.format !== ZPL_FORMAT) {
    return messages.selectedPrefix + " " + messages.previewName;
  }
  return messages.selectedPrefix + " " + displayName(savedMethod.name) + " (" + messages.zpl + ")";
}

function methodDescription(method, messages, description) {
  if (description === false) {
    return "";
  }
  if (description) {
    return description;
  }
  return method.format === ZPL_FORMAT ? messages.zplDescription : messages.previewDescription;
}

function methodDescriptionHTML(method, messages, description) {
  var text = methodDescription(method, messages, description);
  if (!text) {
    return "";
  }
  return "<div class='small text-muted' style='line-height: 1.25;'>" + escapeHtml(text) + "</div>";
}

function methodRowHTML(method, savedMethod, messages, description) {
  var selected = printMethodMatches(savedMethod, method);
  var defaultText = method.is_default && !method.isPreview ? " " + messages.defaultPrinter : "";
  var buttonClass = selected ? "btn-secondary" : "btn-outline-secondary";
  var buttonText = selected ? messages.selected : messages.select;

  return "" +
    "<div class='my-2 d-flex align-items-center'>" +
    "<span class='mr-2' style='flex: 0 0 120px;'>" +
    "<button type='button' class='btn btn-sm " + buttonClass + " " + SELECT_BUTTON_CLASS + "' " +
    "style='min-height: 34px; white-space: nowrap; width: 100%;' " +
    "data-printer-id='" + escapeAttribute(method.id) + "' " +
    "data-printer-format='" + escapeAttribute(method.format) + "' " +
    "data-printer-name='" + escapeAttribute(method.name) + "'>" +
    escapeHtml(buttonText) +
    "</button>" +
    "</span>" +
    "<span style='min-width: 0;'>" +
    "<span class='font-weight-bold'>" + escapeHtml(displayName(method.name)) + escapeHtml(defaultText) + "</span>" +
    methodDescriptionHTML(method, messages, description) +
    "</span>" +
    "</div>";
}

function zplMethod(printer) {
  return {
    id: printer.id,
    name: printer.name,
    format: ZPL_FORMAT,
    is_default: printer.is_default
  };
}

function detectedZPLPrinters(printers) {
  return (printers || []).filter(function(printer) {
    return printerFormat(printer.name) === ZPL_FORMAT;
  });
}

function advancedPrinters(printers) {
  return (printers || []).filter(function(printer) {
    return printerFormat(printer.name) !== ZPL_FORMAT;
  });
}

function advancedPrintersHTML(printers, savedMethod, messages) {
  if (printers.length === 0) {
    return "";
  }

  var selectedAdvanced = printers.some(function(printer) {
    return printMethodMatches(savedMethod, zplMethod(printer));
  });

  return "" +
    "<button type='button' class='btn btn-link btn-sm p-0 mt-2 " + ADVANCED_TOGGLE_CLASS + "'>" +
    escapeHtml(messages.advancedToggle) +
    "</button>" +
    "<div class='" + (selectedAdvanced ? "" : "d-none ") + "mt-2 " + ADVANCED_LIST_CLASS + "'>" +
    "<p class='small text-muted mb-2'>" + escapeHtml(messages.advancedIntro) + "</p>" +
    printers.map(function(printer) {
      return methodRowHTML(zplMethod(printer), savedMethod, messages, false);
    }).join("") +
    "</div>";
}

function renderPrintMethodList(container, printers, messages) {
  var listEl = container.querySelector(".js-chit-chats-print-printer-list");
  if (!listEl) {
    return;
  }

  var savedMethod = readPrintMethodCookie();
  setSelectedPrintMethodMessage(container, savedMethod, messages);

  var rows = [methodRowHTML(previewMethod(messages), savedMethod, messages)];
  rows = rows.concat(detectedZPLPrinters(printers).map(function(printer) {
    return methodRowHTML(zplMethod(printer), savedMethod, messages);
  }));
  rows.push(advancedPrintersHTML(advancedPrinters(printers), savedMethod, messages));
  listEl.innerHTML = rows.join("");
}

function applyPrintMethodSelectionStyle(container, savedMethod, messages) {
  var buttons = container.querySelectorAll("." + SELECT_BUTTON_CLASS);
  buttons.forEach(function(button) {
    var matches = printMethodMatches(savedMethod, methodFromButton(button));
    button.classList.toggle("btn-secondary", matches);
    button.classList.toggle("btn-outline-secondary", !matches);
    button.textContent = matches ? messages.selected : messages.select;
  });
}

function setSelectedPrintMethodMessage(container, savedMethod, messages) {
  var selectedEl = container.querySelector(".js-chit-chats-print-selected-printer");
  if (selectedEl) {
    selectedEl.textContent = selectedPrintMethodText(savedMethod, messages);
  }
}

function toggleAdvancedPrintMethods(container) {
  var list = container.querySelector("." + ADVANCED_LIST_CLASS);
  if (list) {
    list.classList.toggle("d-none");
  }
}

function methodFromButton(button) {
  return {
    id: button.dataset.printerId,
    name: button.dataset.printerName,
    format: button.dataset.printerFormat
  };
}

function printMethodMessages(container) {
  return {
    loading: container.dataset.printersLoading,
    error: container.dataset.printersError,
    selectedPrefix: container.dataset.printersSelectedPrefix,
    selectedEmpty: container.dataset.printersSelectedEmpty,
    zpl: container.dataset.printersZpl,
    previewName: container.dataset.printersPreviewName,
    previewDescription: container.dataset.printersPreviewDescription,
    zplDescription: container.dataset.printersZplDescription,
    advancedToggle: container.dataset.printersAdvancedToggle,
    advancedIntro: container.dataset.printersAdvancedIntro,
    defaultPrinter: container.dataset.printersDefault,
    select: container.dataset.printersSelect,
    selected: container.dataset.printersSelected,
    refresh: container.dataset.printersRefresh,
    refreshing: container.dataset.printersRefreshing
  };
}

function escapeHtml(value) {
  var div = document.createElement("div");
  div.textContent = value || "";
  return div.innerHTML;
}

function escapeAttribute(value) {
  return escapeHtml(value).replace(/'/g, "&#39;").replace(/"/g, "&quot;");
}

export {
  ADVANCED_TOGGLE_CLASS,
  SELECT_BUTTON_CLASS,
  applyPrintMethodSelectionStyle,
  methodFromButton,
  printMethodMessages,
  readPrintMethodCookie,
  renderPrintMethodList,
  setSelectedPrintMethodMessage,
  toggleAdvancedPrintMethods,
  writePrintMethodCookie
};
